import { Moment } from "moment";
import { IBaseShiftEntity, IMemberEntity } from "sh-models";

/**
 * Padding insets (px) around the shift card in DnD whitespace mode.
 * Must stay in sync with the ShiftThemeContainer.scss DnD overrides.
 * Used for coordinate-based hit-testing in ScheduleCell.onMouseDown and
 * PeopleViewGrid.onShouldStartSelection to distinguish card clicks from
 * padding-area clicks.
 */
export const DND_PADDING_TOP = 12;
export const DND_PADDING_LEFT_RIGHT = 10;
export const DND_PADDING_BOTTOM = 6;

/**
 * Module-level singleton that tracks which shift is currently being dragged.
 * Using a module variable rather than dataTransfer.setData() gives typed access
 * to the full shift entity without JSON round-tripping.
 */
interface ShiftDragState {
    shift: IBaseShiftEntity | null;
    member: IMemberEntity | null;
    tagId: string | null;
    /** True once the dragged shift has hovered over at least one valid drop target. */
    hadValidHover: boolean;
}

let _state: ShiftDragState = { shift: null, member: null, tagId: null, hadValidHover: false };

/**
 * True while a drop operation is in-flight (API call pending).
 * Prevents the user from starting a new drag before the previous drop resolves.
 */
let _isDropOperationPending: boolean = false;

/**
 * The ID of the open shift currently being assigned to a member (API in-flight).
 * Only that specific shift is blocked from being dragged again; all other shifts
 * remain draggable while the assignment completes.
 */
let _pendingAssignOpenShiftId: string | null = null;

export function setDragState(shift: IBaseShiftEntity, member: IMemberEntity, tagId: string): void {
    _state = { shift, member, tagId, hadValidHover: false };
}

export function getDragState(): ShiftDragState {
    return _state;
}

export function setHadValidHover(): void {
    _state = { ..._state, hadValidHover: true };
}

export function clearDragState(): void {
    _state = { shift: null, member: null, tagId: null, hadValidHover: false };
    _dayViewGrabOffsetMinutes = 0;
    _overrideDropStartTime = null;
}

export function isDropOperationPending(): boolean {
    return _isDropOperationPending;
}

export function setDropOperationPending(): void {
    _isDropOperationPending = true;
}

export function clearDropOperationPending(): void {
    _isDropOperationPending = false;
}

export function setPendingAssignOpenShiftId(shiftId: string): void {
    _pendingAssignOpenShiftId = shiftId;
}

export function clearPendingAssignOpenShiftId(): void {
    _pendingAssignOpenShiftId = null;
}

export function isOpenShiftAssignPending(shiftId: string): boolean {
    return _pendingAssignOpenShiftId === shiftId;
}

/**
 * The ID of the open shift whose slot-count update (from a shift-to-open-shift conversion
 * or open-shift merge) is currently in-flight. Only that specific open shift cell is blocked
 * as a drop target; all other cells remain valid.
 */
let _pendingTargetOpenShiftId: string | null = null;

export function setPendingTargetOpenShiftId(shiftId: string): void {
    _pendingTargetOpenShiftId = shiftId;
}

export function clearPendingTargetOpenShiftId(): void {
    _pendingTargetOpenShiftId = null;
}

export function isTargetOpenShiftUpdatePending(shiftId: string): boolean {
    return _pendingTargetOpenShiftId === shiftId;
}

/**
 * In day view, how many minutes from the shift's start the user grabbed.
 * Used so that dropping on the source shift cell at a different cursor position
 * moves the shift to the correct new start time rather than being a no-op.
 */
let _dayViewGrabOffsetMinutes: number = 0;

export function setDayViewGrabOffsetMinutes(minutes: number): void {
    _dayViewGrabOffsetMinutes = minutes;
}

export function getDayViewGrabOffsetMinutes(): number {
    return _dayViewGrabOffsetMinutes;
}

/**
 * Computed in onDrop when the cursor lands on the source shift cell in day view.
 * Provides handleShiftDrop with a cursor-derived start time so small moves work.
 */
let _overrideDropStartTime: Moment | null = null;

export function setOverrideDropStartTime(t: Moment): void {
    _overrideDropStartTime = t.clone();
}

export function getAndClearOverrideDropStartTime(): Moment | null {
    const t = _overrideDropStartTime;
    _overrideDropStartTime = null;
    return t?.clone() ?? null;
}
